import React, { useState, useMemo, useRef, useEffect } from "react";
import { TreeView } from "@mui/x-tree-view/TreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import {
  kamerItem,
  kastOpbouw,
  kastItem,
  ModuleTypes,
} from "../../../Shared/sharedInterfaces";
import { getModuleFromName } from "../sharedFunctions";

import * as myTreeIcons from "../../../images/icons/treeicons";
import { Grid, Typography } from "@mui/material";
import { Button } from "react-bootstrap";
import { InnerMapValue2 } from "../calculateFunctions";
import specialItemConfig from "../../config/exceptions/specialItemConfig.json";
import "./kastcomp.css";
import { getTranslations } from "../../../Shared/translations";

const getSpecialConfigKey = (moduleType: string): keyof typeof specialItemConfig | undefined => {
  const normalized = moduleType.toLowerCase();

  if (normalized === "dalim" || normalized === "qdm" || normalized === "qdm01") {
    return "qdm";
  }

  if (normalized === "dmx" || normalized === "ser485/dmx") {
    return "dmx";
  }

  return normalized as keyof typeof specialItemConfig;
};

let globalNodeId = 0;

interface ModuleInstance {
  instanceId: string;
  module: kastOpbouw;
  outputs: kastItem[];
  typeIcon: string;
}

// Shared function that creates modules with outputs
const distributeOutputs = (
  modulesList2: Map<string, Map<string, Map<string, InnerMapValue2>>>,
  outputs: kastItem[],
): ModuleInstance[] => {
  const instances: ModuleInstance[] = [];
  let globalIndex = 0;
  let offsetModules = 0;

  const hasGroup = (g?: string) => g !== undefined && g !== "";

  const outputsWithoutKasten = outputs.filter(
    (o) => o.type !== "kast" && o.type !== "controller",
  );

  // Sort outputsWithoutKasten items so that items with GroupType (modules) comes first
  // This allows us to put these modules at the first place in kasten
  outputsWithoutKasten.sort(
    (a, b) =>
      Number(!hasGroup(a.outputOptions.moduletype)) -
      Number(!hasGroup(b.outputOptions.moduletype)),
  );

  // Extract single outputs (outputs that where added as I/O)
  let singleOutputs: kastItem[] = outputsWithoutKasten
    .filter((o) => o.outputOptions.moduletype === "")
    .map((o) => ({
      ...o,
      outputOptions: {
        ...o.outputOptions,
      },
    }));

  // Sort single I/O's so that items that are created with Op/Neer comes first
  // We do this to be sure that these kind of outputs are allways positioned with 2 linked relais
  singleOutputs.sort(
    (a, b) => Number(a.type !== "ROLO") - Number(b.type !== "ROLO"),
  );

  // Extract outputs that are added as module
  let moduleOutputs: kastItem[] = outputsWithoutKasten
    .filter((o) => o.outputOptions.moduletype !== "")
    .map((o) => ({
      ...o,
      outputOptions: {
        ...o.outputOptions,
      },
    }));

  // Here we create a module an place outputs
  const createOutputModule = (
    module: kastOpbouw,
    outputs: kastItem[],
  ): ModuleInstance | null => {
    let moduleInstance: ModuleInstance | null = null;

    outputs.slice(offsetModules, offsetModules + module.nrofoutputs!);

    if (outputs.length !== 0) {
      moduleInstance = {
        instanceId: `${module.module}-kast${
          module.parent ?? 0
        }-instance${globalIndex++}`, // unique
        module: module,
        typeIcon: module.typeIcon || "",
        outputs: outputs.map((o) => ({ ...o })), // clone outputs to prevent shared references
      };
    }

    return moduleInstance;
  };

  //-------------

  modulesList2.forEach((kast, kastKey) => {
    let thisKastId = Number(kastKey.split("-").pop());
    const thisKastType = kastKey.split("-")[0];
    let kastId = 0;
    let ExtraAdresCtd = false;
    const kastTT = kast;
    //;
    if (thisKastId >= 100) {
      const remainder = thisKastId % 100;
      kastId = Math.floor(thisKastId / 100);
      thisKastId = remainder;
      ExtraAdresCtd = true;
    }
    //console.log("kastid: ", kastId);

    kast.forEach((controller, controllerKey) => {
      let thisControllerId = Number(controllerKey.split("-").pop());
      //console.log(thisControllerId);

      const hasQwiEwModule = Array.from(controller.keys()).some(
        (key) => key.toUpperCase() === "QWI/EW",
      );
      const hasQwiZbModule = Array.from(controller.keys()).some(
        (key) => key.toUpperCase() === "QWI/ZB",
      );

      if (thisKastType === "kastIO") {
        if (ExtraAdresCtd) {
          singleOutputs = singleOutputs.map((o) => ({
            ...o,
            outputOptions: {
              ...o.outputOptions,
              ctdId: thisControllerId,
            },
          }));
        }
      } else if (thisKastType === "kastMOD") {
        if (ExtraAdresCtd) {
          moduleOutputs = moduleOutputs.map((o) => ({
            ...o,
            outputOptions: {
              ...o.outputOptions,
              ctdId: thisControllerId,
            },
          }));
        }
      }

      // Track outputs already consumed by earlier module types for this controller.
      const consumedOutputKeys = new Set<string>();
      const kastItemKey = (o: kastItem): string =>
        `${o.label}|${o.type}|${o.outputOptions.ctdId}|${o.outputOptions.kastId}|${o.outputOptions.moduletype}`;

      controller.forEach((moduleProperties, moduleKey) => {
        const normalizedModuleKey = moduleKey.toUpperCase();
        const isQwiEwModule = normalizedModuleKey === "QWI/EW";
        const isQwiZbModule = normalizedModuleKey === "QWI/ZB";
        const isQweChildModule =
          (normalizedModuleKey.includes("QWE") || normalizedModuleKey.includes("COMWE")) && !isQwiEwModule;
        const isQwzChildModule =
          normalizedModuleKey.includes("COMWZ") && !isQwiZbModule;

        // Exception: QWE-keys become children of QWI/EW instead of standalone modules.
        if (isQweChildModule && hasQwiEwModule) {
          return;
        }

        // Exception: COMWZ-keys become children of QWI/ZB instead of standalone modules.
        if (isQwzChildModule && hasQwiZbModule) {
          return;
        }

        const moduleDefinition = getModuleFromName(moduleKey) as
          | ModuleTypes
          | undefined;

        // Modules met busConsumption -1 niet tonen in kasten/controllers.
        if (moduleDefinition?.busConsumption === -1) {
          return;
        }

        // Here we should add a module with name (moduleKey) and properties (moduleProperties)
        const moduleName = moduleProperties.modType;
        // Ene keer is modType = "DIM" / "DIM02SA"

        const aantalModules = moduleProperties.used;
        const ios2Use = moduleProperties.usedadresses;
        const vrij = moduleProperties.vrij;
        const modprops = getModuleFromName(moduleName);

        let moduleType = moduleName;
        if (modprops) {
          moduleType = modprops?.type;
        }
        //Problem: for DIM02SA modules, type is DIM02SA, we should get DIM from the properties

        let ios: kastItem[] = [];
        const matchesModuleOutputType = (outputType: string): boolean => {
          if (isQwiEwModule && outputType.includes("COMWE")) {
            return true;
          }

          if (isQwiZbModule && outputType.includes("COMWZ")) {
            return true;
          }

          return [moduleType, "ROLO"].includes(outputType);
        };

        if (thisKastType === "kastIO") {
          //o.outputOptions.moduletype = ""

          singleOutputs.sort((a, b) => {
            if (a.type === moduleType && b.type !== moduleType) return -1;
            if (a.type !== moduleType && b.type === moduleType) return 1;
            return 0;
          });

          ios = singleOutputs.filter(
            (o) =>
              matchesModuleOutputType(o.type) &&
              //o.type === moduleType &&
              o.outputOptions.moduletype === "" &&
              o.outputOptions.kastId === thisKastId &&
              o.outputOptions.ctdId === thisControllerId &&
              !consumedOutputKeys.has(kastItemKey(o)),
          );
        } else if (thisKastType === "kastMOD") {
          moduleOutputs.sort((a, b) => {
            if (a.type === moduleType && b.type !== moduleType) return -1;
            if (a.type !== moduleType && b.type === moduleType) return 1;
            return 0;
          });

          ios = moduleOutputs.filter(
            (o) =>
              //o.outputOptions.moduletype === moduleType &&
              matchesModuleOutputType(o.type) &&
              o.outputOptions.kastId === thisKastId &&
              o.outputOptions.ctdId === thisControllerId &&
              !consumedOutputKeys.has(kastItemKey(o)),
          );
        }
        //-----------------

        for (let aantal = 0; aantal < aantalModules; aantal++) {
          const modParameters = moduleDefinition as ModuleTypes;
          let contMaxIOs = modParameters!.outputs;
          const moduleBusTypeKey = getSpecialConfigKey(moduleType);
          const moduleBusConfig = moduleBusTypeKey
            ? (specialItemConfig[moduleBusTypeKey] as any)
            : undefined;
          const isWeightedBus = moduleBusConfig?.mode === "weightedChannels";
          const isDualLimitBus = moduleBusConfig?.mode === "dualLimit";

          if (isWeightedBus) {
            contMaxIOs = moduleBusConfig.maxChannelsPerModule;
          } else if (isDualLimitBus) {
            contMaxIOs = moduleBusConfig.maxOutputsPerModule;
          }

          const getOutputUsage = (
            output: kastItem,
          ): { slots: number; groupSlots: number } => {
            if (!moduleBusConfig) {
              return { slots: 1, groupSlots: 0 };
            }

            const typeField = moduleBusConfig.typeField;
            const defaultType = moduleBusConfig.defaultType;
            const rawType =
              (output.outputOptions as any)[typeField] ??
              (output.outputOptions as any).dmxType ??
              (output.outputOptions as any).DmxType ??
              defaultType;
            const typeKey = String(rawType);
            const typeConfig = moduleBusConfig.types?.[typeKey] ?? {};

            if (isWeightedBus) {
              return {
                slots: typeConfig.channelsPerItem ?? 1,
                groupSlots: 0,
              };
            }

            if (isDualLimitBus) {
              return {
                slots: typeConfig.outputUnits ?? 1,
                groupSlots: typeConfig.groupUnits ?? 0,
              };
            }

            return { slots: 1, groupSlots: 0 };
          };

          // Get all outputs for these modules
          const { outputs, indexesToRemove } = ios.reduce<{
            outputs: kastItem[];
            indexesToRemove: number[];
          }>(
            (acc, o, index) => {
              const normalizedOutputType = String(o.type || "").toUpperCase();
              if (o.type === "ROLO") {
                // If we have Rol Up/Down io's change the to REL, this way they can be added to Relais modules
                o.type = "REL";
              }
              if (
                o.type === modParameters.type ||
                (isQwiEwModule && normalizedOutputType.includes("COMWE")) ||
                (isQwiZbModule && normalizedOutputType.includes("COMWZ"))
              ) {
                acc.outputs.push(o);
                acc.indexesToRemove.push(index);
              }
              return acc;
            },
            { outputs: [], indexesToRemove: [] },
          );

          // Create the module
          const myMod: kastOpbouw = {
            module: moduleKey,
            type: "module",
            nrofoutputs: contMaxIOs,
            parentkast: kastKey,
            typeIcon: modParameters.icon,
            //parent: module = kast-x -> x = parent
          };

          

          // Get outputs for this module; for special buses (DMX) we use weighted slots.
          let outputsToAdd: kastItem[] = [];
          let selectedOutputIndexes: number[] = [];
          let usedSlots = 0;
          let usedGroupSlots = 0;

          for (let i = 0; i < outputs.length; i++) {
            const out = outputs[i];
            const usage = getOutputUsage(out);
            const maxGroups = moduleBusConfig?.maxGroupsPerModule ?? Number.MAX_SAFE_INTEGER;

            if (usedSlots + usage.slots > contMaxIOs) {
              break;
            }

            if (isDualLimitBus && usedGroupSlots + usage.groupSlots > maxGroups) {
              continue;
            }

            outputsToAdd.push(out);
            usedSlots += usage.slots;
            usedGroupSlots += usage.groupSlots;
            selectedOutputIndexes.push(i);
          }

          const thisMod: ModuleInstance | null = createOutputModule(
            myMod,
            outputsToAdd,
          );

          if (thisKastType === "kastIO") {
            // Remove used outputs from this ios slice and register as consumed.
            selectedOutputIndexes
              .map((outputIndex) => indexesToRemove[outputIndex])
              .sort((a, b) => b - a)
              .forEach((index) => {
                consumedOutputKeys.add(kastItemKey(ios[index]));
                ios.splice(index, 1);
              });

            // Add module to instances
            //instances.push(thisMod!);
          } else if (thisKastType === "kastMOD") {
            // Remove used outputs from this ios slice and register as consumed.
            selectedOutputIndexes
              .map((outputIndex) => indexesToRemove[outputIndex])
              .sort((a, b) => b - a)
              .forEach((index) => {
                consumedOutputKeys.add(kastItemKey(ios[index]));
                ios.splice(index, 1);
              });

            // Add module to instances
            //instances.push(thisMod!);
          }

          // Add module to instances
          instances.push(thisMod!);
        }
      });
    });
  });
  
  return instances;
};

// Build treeview kasten layout
const buildPhysicalTree = (
  controllers: kastItem[],
  kasten: kastItem[],
  modulesWithOutputs: ModuleInstance[],
): kamerItem[] => {
  const tree: kamerItem[] = [];

  kasten.forEach((kast, kIndex) => {
    const kastNode: kamerItem = {
      id: `kast-${kast.outputOptions.kastId ?? kIndex}`,
      nodeid: `kast-${kast.outputOptions.kastId ?? kIndex}`,
      name: kast.label,
      type: "kast",
      typeIcon: "electric",
      children: [],
    };

    // Modules assigned to this kast
    // ToDo: sometimes we have an null type item in our array, i don't know why, but we skip it with mi !== null
    const modulesForKast = modulesWithOutputs.filter(
      (mi) =>
        mi !== null &&
        (mi.module.parentkast === "kastIO-" + kast.outputOptions.kastId ||
          mi.module.parentkast === "kastMOD-" + kast.outputOptions.kastId ||
          mi.module.parentkast ===
            "kastIO-" + (kast.outputOptions.kastId + 100) ||
          mi.module.parentkast ===
            "kastMOD-" + (kast.outputOptions.kastId + 100)),
    );

    modulesForKast.forEach((modInst, idx) => {
      const modNodeId = `physicalmodule-${modInst.instanceId}`; //-${idx}`;

      const moduleNode: kamerItem = {
        id: modNodeId,
        nodeid: modNodeId,
        name: `${modInst.module.module} `,
        type: "module",
        typeIcon: modInst.module.typeIcon,
        children: modInst.outputs.map((o, idx) => ({
          id: `output-${idx}-${modNodeId}`,
          nodeid: `output-${idx}-${modNodeId}`,
          name: o.label,
          type: "output",
          typeIcon: o.typeIcon,
          nodeData: o,
          children: [],
        })),
      };

      kastNode.children!.push(moduleNode);
    });

    controllers.forEach((controller, kIndex) => {
      if (controller.outputOptions.kastId === kast.outputOptions.kastId) {
        const controllerNode: kamerItem = {
          id: `kast-ctd${controller.outputOptions.ctdId ?? kIndex}`,
          nodeid: `kast-ctd${controller.outputOptions.ctdId ?? kIndex}`,
          name: controller.label,
          type: "controller",
          typeIcon: "electric",
          children: [],
        };
        kastNode.children!.push(controllerNode);
      }
    });

    tree.push(kastNode);
  });

  return tree;
};

// Build treeview controllers layoout
const buildLogicalTree = (
  controllers: kastItem[],
  modulesWithOutputs: ModuleInstance[],
  controllerMap: Map<number, ControllerData> | undefined,
) => {
  const controllerMap1 = new Map<number, kamerItem>();
  const tree: kamerItem[] = [];
  //let ctdArray: number[] = [];

  const ctdMap = new Map<number, number>();
  function sortCtds(arr: kastItem[]) {
    return arr.sort((a, b) => {
      const aMod = a.outputOptions.ctdId % 100;
      const bMod = b.outputOptions.ctdId % 100;
      if (aMod !== bMod) return aMod - bMod;
      return a.outputOptions.ctdId - b.outputOptions.ctdId;
    });
  }

  function sortModules(arr: ModuleInstance[]) {
    return arr.sort((a, b) => {
      const aMod = a.outputs[0].outputOptions.ctdId % 100;
      const bMod = b.outputs[0].outputOptions.ctdId % 100;
      if (aMod !== bMod) return aMod - bMod;
      return (
        a.outputs[0].outputOptions.ctdId - b.outputs[0].outputOptions.ctdId
      );
    });
  }

  const sortedCtds = sortCtds(controllers);

  // Create list for controllers
  sortedCtds.forEach((controller, kIndex) => {
    const controllerNode: kamerItem = {
      id: `ctd-${controller.outputOptions.ctdId ?? kIndex}`,
      nodeid: `ctd-${controller.outputOptions.ctdId ?? kIndex}`,
      name: controller.label,
      type: "controller",
      typeIcon: "electric",
      children: [],
    };
    tree.push(controllerNode);
    controllerMap1.set(controller.outputOptions.ctdId ?? 0, controllerNode);
    //ctdArray.push(controller.outputOptions.ctdId);
    const modsOnThisCtd = controllerMap
      ?.get(controller.outputOptions.ctdId)
      ?.values.get("mods");
    ctdMap.set(controller.outputOptions.ctdId, modsOnThisCtd || 1);
  });

  //controllerMap?.get()
  // const aantalUpgradedCtds = ctdArray.filter((n) => n >= 100).length;
  let modCounter = 1;
  let ctdId2 = 0;
  // Assign module instances to controllers
  /*
  const modo = modulesWithOutputs.sort(
    (a, b) =>
      a.outputs[0].outputOptions.ctdId - b.outputs[0].outputOptions.ctdId,
  );
*/

  const filtered = modulesWithOutputs.filter(Boolean);
  const modo = sortModules(filtered);

  function getMapAtIndex(index: number) {
    const iter = ctdMap.entries();
    //debugger
    for (let i = 0; i < index; i++) iter.next();
    return iter.next().value;
  }

  let ctdCounter = 1;

  modo.forEach((modInst, idx) => {
    if (modInst === null) return;
    if (!modInst.outputs) return;

    let ctdId = modInst.outputs[0].outputOptions?.ctdId ?? 0;

    /*
    if (modCounter === ctdMap.get(ctdId)!) {
      const ctdMapK = getMapAtIndex(ctdCounter);
      if (ctdMapK) {
        ctdId = ctdMapK[0];
      }

      modCounter = 1;
      ctdCounter++;
    }
      
    modCounter++;
    */
    const parentController = controllerMap1.get(ctdId);

    if (!parentController) return;

    // ✅ Use a truly unique node id for each module instance
    const modNodeId = `logicmodule-${modInst.module}-${modInst.instanceId}-${idx}`;

    const modNode: kamerItem = {
      id: modNodeId,
      nodeid: modNodeId,
      name: `${modInst.module.module} `,
      type: "module",
      typeIcon: modInst.module.typeIcon,
      children: modInst.outputs.map((o, idx) => ({
        id: `output-${idx}-${modNodeId}`,
        nodeid: `output-${idx}-${modNodeId}`,
        name: o.label,
        type: "output",
        typeIcon: o.typeIcon,
        nodeData: o,
        children: [],
      })),
    };
    //console.log(parentController.id, " - " , modNode)
    parentController.children!.push(modNode);
  });

  return tree;
};

interface ControllerData {
  label: string;
  values: Map<string, number>;
}
interface MyProps {
  db2: kastItem[];
  modulesList2: Map<string, Map<string, Map<string, InnerMapValue2>>>;
  addKastToOverzicht: () => void;
  addCtdToOverzicht: () => void;
  setCtd: (id: string) => void;
  setKast: (id: string) => void;
  ctdId: Number;
  kastId: Number;
  controllerMap: Map<number, ControllerData> | undefined;
}

const DynamicTreeViewc = ({
  db2,
  modulesList2,
  addKastToOverzicht,
  addCtdToOverzicht,
  setCtd,
  setKast,
  ctdId,
  kastId,
  controllerMap,
}: MyProps) => {
  const t = getTranslations();
  const cleanedDb2 = db2.filter((item) => item.label !== undefined);
  const controllers = db2.filter((item) => item.type === "controller");
  const kasten = db2.filter((item) => item.type === "kast");

  const [selectedKast, setSelectedKast] = React.useState<Number>();
  const [selectedCtd, setSelectedCtd] = React.useState<Number>();

  const ctdIds = controllers.map(
    (controller) => controller.outputOptions.ctdId,
  );
  let updatedControllers = controllers;
debugger
  /*
  // Check for duplicate ctdId's and create new virtual ctdId's for them
  */
  ctdIds.forEach((id) => {
    let extraIndex = 1;

    while (controllerMap!.has(extraIndex * 100 + id)) {
      const ctdid = extraIndex * 100 + id;
      const controller = controllers.find((c) => c.outputOptions.ctdId === id);
      if (!controller) break;

      const duplicated: kastItem = {
        ...controller,
        label: controller.label + "/" + extraIndex,
        type: controller.type,
        typeIcon: controller.typeIcon,
        outputOptions: {
          ...controller.outputOptions,
          ctdId: ctdid,
        },
      };

      updatedControllers = [...updatedControllers, duplicated];
      extraIndex++;
    }
  });

  // ToDo - We just added a 'virtual' ctd (added in code to upgrade CTD fro adresses)
  // We should build from updatedControllers instead of controllers
  // Then we should also

  useEffect(() => {
    if (ctdId !== selectedCtd) {
      setSelectedCtd(ctdId);
    }
  }, [ctdId]);

  useEffect(() => {
    if (kastId !== selectedKast) {
      setSelectedKast(kastId);
    }
  }, [kastId]);

  // Build modules with ouputs
  const modulesWithOutputs: ModuleInstance[] = distributeOutputs(
    modulesList2,
    cleanedDb2,
  );

  // Here we handle our kast/ctd clicked
  const handleTreeItemClicked = (node: kamerItem, event: any) => {
    const id = node.id.slice(node.id.lastIndexOf("-") + 1);
    const isNumber = /^\d+$/.test(id);

    const parentCTD = Number(id) % 10
    console.log(parentCTD)
    if (node.type == "controller") {
      if (isNumber) {
        setCtd(String(parentCTD));
      }
      //setCtd(node.id)
      setSelectedCtd(Number(id));
    } else if (node.type == "kast") {
      setKast(id);
      setSelectedKast(Number(id));
    }
  };

  const isNodeSelected = (id: string) => {
    const [type, idPart] = id.split("-");
    const idNumber = Number(idPart);

    if (type === "ctd") return selectedCtd === idNumber;
    if (type === "kast") return selectedKast === idNumber;

    return false;
  };

  // Here we render out treeview
  const renderTree = (node: kamerItem): React.ReactNode => (
    <TreeItem
      key={node.nodeid}
      nodeId={node.nodeid}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleTreeItemClicked(node, e);
      }}
      sx={{
        "& .MuiTreeItem-content": {
          backgroundColor: isNodeSelected(node.id)
            ? "rgba(87, 87, 87, 1)"
            : "inherit",
          cursor: "pointer",
          userSelect: "none",
        },

        // override MUI focus + selected
        "& .MuiTreeItem-content.Mui-focused": {
          backgroundColor: isNodeSelected(node.id)
            ? "rgba(87, 87, 87, 1)"
            : "inherit",
        },
        "& .MuiTreeItem-content.Mui-selected": {
          backgroundColor: isNodeSelected(node.id)
            ? "rgba(87, 87, 87, 1)"
            : "inherit",
        },
      }}
      icon={
        node.typeIcon
          ? myTreeIcons.getTreeIcon2(node.typeIcon, node.typeIcon)
          : undefined
      }
      label={node.name}
    >
      {node.children?.map((child) => renderTree(child))}
    </TreeItem>
  );

  const addKast = () => {
    addKastToOverzicht();
  };

  const addCtd = () => {
    addCtdToOverzicht();
  };

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography>{t.kasten}</Typography>
          <Button className="add-buttons" onClick={addKast}>
            {" "}
            {t.voegKastToe}
          </Button>
          <TreeView>
            {buildPhysicalTree(
              updatedControllers,
              kasten,
              modulesWithOutputs,
            ).map(renderTree)}
          </TreeView>
        </Grid>
        <Grid item xs={6}>
          <Typography>{t.controllers}</Typography>
          <Button className="add-buttons" onClick={addCtd}>
            {" "}
            {t.voegCtdToe}
          </Button>
          <TreeView>
            {buildLogicalTree(
              updatedControllers,
              modulesWithOutputs,
              controllerMap,
            ).map(renderTree)}
          </TreeView>
        </Grid>
      </Grid>
    </div>
  );
};

export default DynamicTreeViewc;
